import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { pdf } from '@react-pdf/renderer';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import LogoUploader from '../components/LogoUploader';
import Button from '../components/ui/Button';
import useDraftSaving from '../hooks/useDraftSaving';

// Import all template forms
import BakkalReportForm from '../templates/BakkalReportForm';
import KurbanReportForm from '../templates/KurbanReportForm';
import KlinikReportForm from '../templates/KlinikReportForm';
import InsaatReportForm from '../templates/InsaatReportForm';
import RestoranReportForm from '../templates/RestoranReportForm';
import NakliyeReportForm from '../templates/NakliyeReportForm';
import DugunReportForm from '../templates/DugunReportForm';
import PazarlamaReportForm from '../templates/PazarlamaReportForm';
import DevletGiderReportForm from '../templates/DevletGiderReportForm';
import KamuMalAlimReportForm from '../templates/KamuMalAlimReportForm';
import OfisGiderReportForm from '../templates/OfisGiderReportForm';
import TerziReportForm from '../templates/TerziReportForm';
import EgitimReportForm from '../templates/EgitimReportForm';
import OtoTamirReportForm from '../templates/OtoTamirReportForm';
import LokalOrganizasyonReportForm from '../templates/LokalOrganizasyonReportForm';
import YerelYonetimReportForm from '../templates/YerelYonetimReportForm';

// Import all PDF templates
import BakkalReportPDF from '../pdf/BakkalReportPDF';
import KurbanReportPDF from '../pdf/KurbanReportPDF';
import KlinikReportPDF from '../pdf/KlinikReportPDF';
import InsaatReportPDF from '../pdf/InsaatReportPDF';
import RestoranReportPDF from '../pdf/RestoranReportPDF';
import NakliyeReportPDF from '../pdf/NakliyeReportPDF';
import DugunReportPDF from '../pdf/DugunReportPDF';
import PazarlamaReportPDF from '../pdf/PazarlamaReportPDF';
import DevletGiderReportPDF from '../pdf/DevletGiderReportPDF';
import KamuMalAlimReportPDF from '../pdf/KamuMalAlimReportPDF';
import OfisGiderReportPDF from '../pdf/OfisGiderReportPDF';
import TerziReportPDF from '../pdf/TerziReportPDF';
import EgitimReportPDF from '../pdf/EgitimReportPDF';
import OtoTamirReportPDF from '../pdf/OtoTamirReportPDF';
import LokalOrganizasyonReportPDF from '../pdf/LokalOrganizasyonReportPDF';
import YerelYonetimReportPDF from '../pdf/YerelYonetimReportPDF';

const ReportBuilderPage = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [logos, setLogos] = useState([]);
  const [formData, setFormData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const { hasDraft, saveDraft, loadDraft, deleteDraft } = useDraftSaving(templateId);
  const [showDraftPrompt, setShowDraftPrompt] = useState(false);

  const templateComponents = {
    bakkal: { form: BakkalReportForm, pdf: BakkalReportPDF },
    kurban: { form: KurbanReportForm, pdf: KurbanReportPDF },
    klinik: { form: KlinikReportForm, pdf: KlinikReportPDF },
    insaat: { form: InsaatReportForm, pdf: InsaatReportPDF },
    restoran: { form: RestoranReportForm, pdf: RestoranReportPDF },
    nakliye: { form: NakliyeReportForm, pdf: NakliyeReportPDF },
    dugun: { form: DugunReportForm, pdf: DugunReportPDF },
    pazarlama: { form: PazarlamaReportForm, pdf: PazarlamaReportPDF },
    devletGider: { form: DevletGiderReportForm, pdf: DevletGiderReportPDF },
    kamuMalAlim: { form: KamuMalAlimReportForm, pdf: KamuMalAlimReportPDF },
    ofisGider: { form: OfisGiderReportForm, pdf: OfisGiderReportPDF },
    terzi: { form: TerziReportForm, pdf: TerziReportPDF },
    egitim: { form: EgitimReportForm, pdf: EgitimReportPDF },
    otoTamir: { form: OtoTamirReportForm, pdf: OtoTamirReportPDF },
    lokalOrganizasyon: { form: LokalOrganizasyonReportForm, pdf: LokalOrganizasyonReportPDF },
    yerelYonetim: { form: YerelYonetimReportForm, pdf: YerelYonetimReportPDF }
  };

  const currentTemplate = templateComponents[templateId];
  const FormComponent = currentTemplate?.form;
  const PDFComponent = currentTemplate?.pdf;

  const handleLogosChange = useCallback((newLogos) => {
    setLogos(newLogos);
  }, []);

  const handleFormDataChange = useCallback((data) => {
    setFormData(data);
  }, []);

  const handleGeneratePDF = async () => {
    if (!formData || !PDFComponent) return;

    setIsGenerating(true);
    try {
      const doc = <PDFComponent data={formData} logos={logos} />;
      const asPdf = pdf(doc);
      const blob = await asPdf.toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${templateId}-report-${Date.now()}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  useEffect(() => {
    // Check for existing draft on mount
    if (hasDraft) {
      setShowDraftPrompt(true);
    }
  }, [hasDraft]);

  const handleLoadDraft = () => {
    const draft = loadDraft();
    if (draft) {
      setFormData(draft.formData);
      setLogos(draft.logos || []);
      setShowDraftPrompt(false);
    }
  };

  const handleDiscardDraft = () => {
    deleteDraft();
    setShowDraftPrompt(false);
  };

  const handleSaveDraft = () => {
    const success = saveDraft(formData, logos);
    if (success) {
      alert('Draft saved successfully!');
    } else {
      alert('Error saving draft. Please try again.');
    }
  };

  if (!currentTemplate) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">Template Not Found</h2>
          <Button onClick={() => navigate('/')}>Back to Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen w-full flex-col bg-background-light dark:bg-background-dark">
      <Header />

      {/* Draft Prompt Modal */}
      {showDraftPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md mx-4 shadow-2xl">
            <h3 className="text-xl font-bold text-[#111418] dark:text-white mb-4">Draft Found</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              We found a saved draft for this template. Would you like to continue where you left off?
            </p>
            <div className="flex gap-3">
              <Button onClick={handleLoadDraft} variant="primary" fullWidth>
                Load Draft
              </Button>
              <Button onClick={handleDiscardDraft} variant="outline" fullWidth>
                Start Fresh
              </Button>
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 p-6 lg:p-10 overflow-y-auto">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-grow">
              <div className="flex flex-wrap justify-between gap-3 mb-8">
                <div>
                  <h1 className="text-[#111418] dark:text-white text-4xl font-black leading-tight tracking-[-0.033em]">
                    {t(`templates.${templateId}.title`)}
                  </h1>
                  <p className="text-gray-600 dark:text-gray-400 mt-2">
                    {t(`templates.${templateId}.description`)}
                  </p>
                </div>
                <Button onClick={() => navigate('/')} variant="ghost" icon="arrow_back">
                  Back
                </Button>
              </div>

              {FormComponent && (
                <FormComponent onDataChange={handleFormDataChange} />
              )}
            </div>

            <div className="w-full lg:w-80 lg:shrink-0">
              <div className="sticky top-10 space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
                  <h3 className="text-lg font-bold text-[#111418] dark:text-gray-100 mb-4">Actions</h3>
                  <div className="space-y-3">
                    <Button
                      onClick={handleGeneratePDF}
                      disabled={!formData || isGenerating}
                      icon="picture_as_pdf"
                      fullWidth
                    >
                      {isGenerating ? 'Generating...' : t('form.generatePDF')}
                    </Button>
                    <Button
                      onClick={handlePrint}
                      variant="outline"
                      icon="print"
                      fullWidth
                    >
                      {t('form.print')}
                    </Button>
                    <Button
                      onClick={handleSaveDraft}
                      disabled={!formData}
                      variant="ghost"
                      icon="draft"
                      fullWidth
                    >
                      {t('form.saveDraft')}
                    </Button>
                  </div>
                </div>

                <LogoUploader onLogosChange={handleLogosChange} />
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ReportBuilderPage;
